import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Grid, Typography } from '@mui/material';
import { BRAND, brown, orange, useEnglishPage } from './enCommon';
import TOPICS from './topics.json';

/* 영문 첫 화면 (/en). 글을 고치면 prerender/build_prerender.py 의 EN_HOME 도 같이 */
export const EN_HOME_TITLE = 'Charter Bus Rental in Korea | EK Hana Tour';
export const EN_HOME_DESC =
  'Private charter buses with driver anywhere in Korea — airport transfers, Seoul city tours and multi-day trips. 25, 28 and 45-seat coaches. Free quote by email.';

const BUSES = [
  { seats: '45-seater', name: 'Large coach', img: '/image/bigBusExterior.jpg?v=2', text: 'The most popular choice for groups of 30–45 people.' },
  { seats: '28-seater', name: 'Limousine coach', img: '/image/limousineExterior.jpg?v=3', text: 'Premium seats and facilities for groups of 24–28.' },
  { seats: '25-seater', name: 'Mini bus', img: '/image/smallBusExterior.jpg?v=2', text: 'Economical and easy to move around for small groups.' },
];


const STEPS = [
  ['Send your plan', 'Tell us the dates, places and number of passengers. You can attach your itinerary.'],
  ['Get a quote by email', 'We check bus availability and email you a quotation in English.'],
  ['Confirm and travel', 'Confirm by email and our driver will meet your group on the day.'],
];

export default function EnMain() {
  useEnglishPage(EN_HOME_TITLE, EN_HOME_DESC);

  return (
    <Box component='main' lang='en'>
      <Box
        sx={{
          minHeight: { xs: 220, md: 420 },
          backgroundImage: "linear-gradient(rgba(60, 45, 30, 0.45), rgba(60, 45, 30, 0.25)), url('/image/main.jpg')",
          backgroundSize: 'cover', backgroundPosition: 'center',
          display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2, textAlign: 'center',
        }}
      >
        <Box>
          <Typography component='h1' sx={{ fontSize: { xs: 26, md: 46 }, fontWeight: 800, color: '#fff', textShadow: '0 2px 10px rgba(0,0,0,0.4)' }}>
            Charter Bus Rental in Korea
          </Typography>
          <Typography sx={{ mt: 1, fontSize: { xs: 15, md: 21 }, fontWeight: 600, color: '#fff', textShadow: '0 1px 6px rgba(0,0,0,0.5)' }}>
            Private bus with professional driver · Airport transfers · Tours anywhere in Korea
          </Typography>
          <Button component={RouterLink} to='/en/center' variant='contained'
            sx={{ mt: 3, px: 4, py: 1.3, fontSize: { xs: 16, md: 18 }, fontWeight: 800, bgcolor: orange, '&:hover': { bgcolor: '#d98a30' } }}>
            Get a free quote by email
          </Button>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 4, md: 6 } }}>
        <Typography component='h2' sx={{ fontSize: { xs: 22, md: 30 }, fontWeight: 800, color: brown, textAlign: 'center' }}>
          Our buses
        </Typography>
        <Grid container spacing={2.5} sx={{ mt: 1 }}>
          {BUSES.map((b) => (
            <Grid item xs={12} md={4} key={b.seats}>
              <Box component={RouterLink} to='/en/bus' sx={{ display: 'block', textDecoration: 'none', color: brown, border: '1px solid #eee', borderRadius: 3, overflow: 'hidden', height: '100%' }}>
                <Box component='img' src={b.img} alt={`${b.seats} ${b.name}`} loading='lazy' sx={{ width: '100%', aspectRatio: '16 / 10', objectFit: 'cover', display: 'block' }} />
                <Box sx={{ p: 2 }}>
                  <Typography component='h3' sx={{ fontWeight: 800, fontSize: 19 }}>{b.seats} <Box component='span' sx={{ fontWeight: 500, fontSize: 15 }}>{b.name}</Box></Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 15 }}>{b.text}</Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Typography component='h2' sx={{ mt: { xs: 5, md: 7 }, fontSize: { xs: 22, md: 30 }, fontWeight: 800, color: brown, textAlign: 'center' }}>
          What we do
        </Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* 주제별 페이지로 이어진다(topics.json) — 구글이 따라 들어가는 길이기도 하다 */}
          {TOPICS.map((t) => (
            <Grid item xs={12} sm={6} key={t.slug}>
              <Box component={RouterLink} to={`/en/${t.slug}`}
                sx={{ display: 'block', textDecoration: 'none', p: 2.5, bgcolor: '#FFFDF6', borderLeft: `4px solid ${orange}`, borderRadius: 2, height: '100%' }}>
                <Typography component='h3' sx={{ fontWeight: 800, color: brown, fontSize: 18 }}>{t.card} ›</Typography>
                <Typography sx={{ mt: 0.5, color: brown }}>{t.cardText}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Typography component='h2' sx={{ mt: { xs: 5, md: 7 }, fontSize: { xs: 22, md: 30 }, fontWeight: 800, color: brown, textAlign: 'center' }}>
          How it works
        </Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {STEPS.map(([t, d], i) => (
            <Grid item xs={12} md={4} key={t}>
              <Box sx={{ p: 2.5, textAlign: 'center', border: '1px solid #eee', borderRadius: 3, height: '100%' }}>
                <Typography sx={{ fontSize: 30, fontWeight: 800, color: orange }}>{i + 1}</Typography>
                <Typography component='h3' sx={{ fontWeight: 800, color: brown, fontSize: 18 }}>{t}</Typography>
                <Typography sx={{ mt: 0.5, color: brown }}>{d}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: { xs: 5, md: 7 }, textAlign: 'center' }}>
          <Typography sx={{ color: brown, fontSize: { xs: 16, md: 18 } }}>
            {BRAND} is operated by EK Tour Co., Ltd., a licensed general travel agency in Korea.
          </Typography>
          <Button component={RouterLink} to='/en/center' variant='contained'
            sx={{ mt: 2, px: 4, py: 1.2, fontWeight: 800, bgcolor: orange, '&:hover': { bgcolor: '#d98a30' } }}>
            Request a quote
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
